import type { GASetupDatalayerParamWishlist } from '@integration-layer/schemas/GAEvents/dataLayerSetup'
import type { WishlistView } from '@integration-layer/composables/useGAWishlistEvents'
import type {
  WishlistItem,
  WishlistStorageItem,
} from '@integration-layer/utils/wishlist'

/**
 * Parameters for adding or removing a wishlist item
 */
type AddOrRemoveItemParams = {
  item: Omit<WishlistItem, 'notifyMe'>
  view?: WishlistView
  forceRefresh?: boolean
}

/**
 * Composable for managing wishlist functionality
 *
 * @returns {Object} An object containing methods and states for wishlist management
 */
export const useWishlist = () => {
  const configs = useConfigs()
  const appConfig = useAppConfig()
  const language = appConfig.currentLanguage
  const algoliaIndex = appConfig.currentAlgoliaIndex
  const { $cl } = useNuxtApp()
  const { isLogged } = useLogin()
  const { addNotification, deleteNotification } = useWishlistFeedback()
  const { dispatchAddToWishlistEvent, dispatchRemoveFromWishlistEvent } =
    useGAWishlistEvents()
  const { trackAddToWishlist } = useAlgoliaInsights()

  /**
   * Reactive state for storing wishlist items
   * @type {Ref<WishlistItem[]>}
   */
  const wishlistItemsState = useState<WishlistItem[]>(
    'WISHLIST_STATE',
    () => []
  )

  /**
   * Reactive state for storing deleted wishlist items
   * @type {Ref<WishlistItem[]>}
   */
  const deletedWishlistItems = useState<WishlistItem[]>(
    'DELETED_WISHLIST_ITEMS',
    () => []
  )

  /**
   * Reactive state indicating wishlist loading status
   * @type {Ref<boolean>}
   */
  const isWishlistLoading = useState('IS_WISHLIST_LOADING', () => true)

  /**
   * Reactive state indicating if all items are being deleted
   * @type {Ref<boolean>}
   */
  const isDeletingAll = useState('IS_DELETING_ALL', () => false)

  /**
   * Determines the appropriate wishlist storage based on user login status
   * @type {Object}
   */
  const wishlistStorage = isLogged.value
    ? authWishlistStorage
    : guestWishlistStorage

  /**
   * Fetches product data for wishlist items
   *
   * @param {WishlistStorageItem[]} storedItems - Items stored in wishlist
   * @returns {Promise<WishlistItem[]>} Mapped wishlist items with product details
   */
  const fetchProductsData = async (storedItems: WishlistStorageItem[]) => {
    const productCodes = storedItems.map(({ productCode }) => productCode)
    const algoliaProducts = productCodes.length
      ? await $fetch(`/api/getProducts/${algoliaIndex}`, {
          query: {
            mfcList: productCodes.join(','),
          },
        })
      : []

    const filteredAlgoliaProducts = algoliaProducts.filter(isNonNullable)
    const skuCodes = filteredAlgoliaProducts.reduce<string[]>(
      (acc, { size }) => {
        const skus = size.flatMap(({ SKU }) => SKU ?? [])
        acc.push(...skus)
        return acc
      },
      []
    )

    //Api call inside the ternary to not trigger it beforehand if skuCodes.length is 0
    const skus = skuCodes.length
      ? (
          await Promise.all(
            chunk(skuCodes, 24).map(skuCodesChunk =>
              $cl.skus.list({
                include: [
                  'prices',
                  'stock_items',
                  'stock_items.reserved_stock',
                ],
                filters: {
                  code_in: skuCodesChunk?.join() ?? '',
                },
                pageSize: 24,
              })
            )
          )
        ).flat()
      : []

    return mapWishlistItems(
      storedItems,
      filteredAlgoliaProducts,
      skus,
      configs.value.country,
      language
    )
  }

  /**
   * Initializes the wishlist by fetching stored items
   */
  const initWishlist = async () => {
    try {
      isWishlistLoading.value = true
      const wishlist = await wishlistStorage.get()
      wishlistItemsState.value = await fetchProductsData(wishlist)
    } catch (e) {
      console.error('init wishlist error:', e)
    } finally {
      isWishlistLoading.value = false
    }
  }

  /**
   * Google Analytics entity for wishlist tracking
   * @type {Ref<GASetupDatalayerParamWishlist>}
   */
  const wishlistEntity = useGAEntity<GASetupDatalayerParamWishlist>(
    'GA_DL_SETUP_WISHLIST'
  )

  /**
   * Computed property for all wishlist items
   * @type {ComputedRef<WishlistItem[]>}
   */
  const wishlistItems = computed(() => wishlistItemsState.value ?? [])

  /**
   * Computed property for raw wishlist items with country information
   * @type {ComputedRef<WishlistStorageItem[]>}
   */
  const rawWishlist = computed(() =>
    mapStorageItems(wishlistItems.value, configs.value.country)
  )

  /**
   * Computed property for wishlist items sorted by brand
   * @type {ComputedRef<Map<string, WishlistItem[]>>}
   */
  const wishlistItemsByBrand = computed(() =>
    sortItemsByBrand(wishlistItems.value, item => item.productBrand)
  )

  /**
   * Computed property for total number of products in wishlist
   * @type {ComputedRef<number>}
   */
  const nProducts = computed(() => wishlistItems.value.length)

  /**
   * Computed property to check if wishlist is empty
   * @type {ComputedRef<boolean>}
   */
  const isWishlistEmpty = computed(() => !nProducts.value)

  /**
   * Computed property for product codes in wishlist
   * @type {ComputedRef<{[key: string]: boolean}>}
   */
  const wishlistProductCodes = computed(() =>
    rawWishlist.value.reduce<{ [key: string]: boolean }>(
      (acc, { productCode }) => {
        if (!acc[productCode]) acc[productCode] = true
        return acc
      },
      {}
    )
  )

  /**
   * Merges guest wishlist with authenticated user's wishlist
   */
  const mergeWishlists = async () => {
    if (!isLogged.value) return
    const guestWishlist = await guestWishlistStorage.get()
    const itemsToAdd = diffWishlists(guestWishlist, rawWishlist.value)
    if (!itemsToAdd.length) return
    const wishlistItems = await fetchProductsData(itemsToAdd)
    await addItems(wishlistItems)
    await guestWishlistStorage.delete()
  }

  /**
   * Optimistically updates wishlist state
   *
   * @param {WishlistItem[]} wishlistItems - Updated wishlist items
   */
  const wishlistOptimisticUpdateHandle = async (
    wishlistItems: WishlistItem[]
  ) => {
    const wishlistBeforeUpdate = [...wishlistItemsState.value]
    try {
      // optimistic update
      wishlistItemsState.value = wishlistItems
      await wishlistStorage.createOrUpdate([...rawWishlist.value])
    } catch (e) {
      console.error(e)
      wishlistItemsState.value = [...wishlistBeforeUpdate]
    }
  }

  /**
   * Updates 'notify me' status for a product in wishlist
   */
  const updateNotifyMe = useDebounceFn(
    async (productCode: string, notifyMe: boolean) => {
      const updatedWishlistItems = wishlistItemsState.value.map(item =>
        item.productSku === productCode
          ? {
              ...item,
              notifyMe,
            }
          : item
      )
      await wishlistOptimisticUpdateHandle(updatedWishlistItems)
    },
    250
  )

  /**
   * Adds multiple items to the wishlist
   *
   * @param {WishlistItem[]} items - Items to add
   */
  const addItems = async (items: WishlistItem[]) => {
    if (!items.length) return
    const itemsWithNotifyMe = items.map(item => ({
      ...item,
      ...(item.productSku ? { notifyMe: true } : {}),
      productBrand: formatBrand(item.productBrand),
    }))
    const updateItems = [...wishlistItemsState.value, ...itemsWithNotifyMe]
    await wishlistOptimisticUpdateHandle(updateItems)
  }

  /**
   * Adds a single item to the wishlist
   *
   * @param {WishlistItem} item - Item to add
   */
  const addItem = async (item: WishlistItem) => {
    deleteNotification(item.productCode)
    await addItems([item])
    addNotification(item, WishlistSnackbarAction.addToWishlist)
  }

  /**
   * Restores a previously removed item to the wishlist
   *
   * @param {WishlistItem | null} item - Item to restore
   */
  const restoreItem = async (item: WishlistItem | null) => {
    if (!item) return
    deleteNotification(item.productCode)
    await addItems([item])
  }

  /**
   * Removes an item from the wishlist
   *
   * @param {string} productCode - Product code to remove
   * @param {boolean} [disableNotification=false] - Whether to disable notification
   * @returns {Promise<WishlistItem | undefined>} Removed item
   */
  const removeItem = async (
    productCode: string,
    disableNotification = false
  ) => {
    deleteNotification(productCode)
    const { removedItem, updatedItems } = diffItems(
      wishlistItemsState.value,
      productCode
    )
    await wishlistOptimisticUpdateHandle(updatedItems)

    if (disableNotification) return removedItem
    addNotification(removedItem, WishlistSnackbarAction.removeFromWishlist)
    return removedItem
  }

  /**
   * Creates a deferred item removal mechanism
   *
   * @returns {Object} Methods for managing item removal with undo functionality
   */
  const deferredRemoveItem = () => {
    let updatedWishlist: WishlistStorageItem[] = [...rawWishlist.value]
    const removedItemsFromStorage: {
      pos: number
      value: WishlistStorageItem
    }[] = []
    return {
      removeItemFromStorage: async (item: WishlistItem) => {
        const storageItemIndex = rawWishlist.value.findIndex(
          ({ productCode }) => item.productCode === productCode
        )
        removedItemsFromStorage.push({
          pos: storageItemIndex,
          value: rawWishlist.value[storageItemIndex],
        })
        updatedWishlist = updatedWishlist.filter(
          ({ productCode }) => item.productCode !== productCode
        )
        await wishlistStorage.createOrUpdate(updatedWishlist).catch(e => {
          console.error(e)
        })
      },
      completeRemoveItem: async (item: WishlistItem) => {
        wishlistItemsState.value = wishlistItemsState.value.filter(
          ({ productCode }) => item.productCode !== productCode
        )
      },
      undoRemoveItem: async (item: WishlistItem) => {
        const itemToRestore = removedItemsFromStorage.find(
          ({ value }) => value.productCode === item.productCode
        )
        if (!itemToRestore) return
        const { pos, value } = itemToRestore
        updatedWishlist.splice(pos, 0, value)
        await wishlistStorage.createOrUpdate(updatedWishlist)
      },
    }
  }

  /**
   * Adds or removes an item from the wishlist
   */
  const addOrRemoveItem = useDebounceFn(
    async ({ item, view, forceRefresh = true }: AddOrRemoveItemParams) => {
      const alreadyExists = rawWishlist.value.find(
        ({ productCode }) => item.productCode === productCode
      )
      if (alreadyExists) {
        const removedItem = await removeItem(item.productCode)
        if (removedItem) dispatchRemoveFromWishlistEvent([removedItem])
        return
      }
      const wishlistItems = forceRefresh
        ? await fetchProductsData(
            mapStorageItems([item], configs.value.country)
          )
        : [item]
      dispatchAddToWishlistEvent(wishlistItems, view)
      if (item.objectID || item.productCode) {
        trackAddToWishlist(algoliaIndex, item.objectID || item.productCode)
      }
      await addItem(wishlistItems[0])
    },
    250
  )

  /**
   * Restores previously deleted wishlist items
   */
  const restoreWishlist = async () => {
    try {
      deleteNotification('all')
      await wishlistOptimisticUpdateHandle([...deletedWishlistItems.value])
    } catch (e) {
      console.error(e)
    }
    deletedWishlistItems.value = []
  }

  /**
   * Deletes entire wishlist
   */
  const deleteWishlist = async () => {
    if (!rawWishlist.value.length) return
    try {
      isDeletingAll.value = true
      deletedWishlistItems.value = [...wishlistItemsState.value]

      // optimistic update
      wishlistItemsState.value = []
      dispatchRemoveFromWishlistEvent(deletedWishlistItems.value)
      await wishlistStorage.delete()
      addNotification(null, WishlistSnackbarAction.removeAll)
    } catch (e) {
      console.error(e)
      restoreWishlist()
    } finally {
      isDeletingAll.value = false
    }
  }

  /**
   * Manages wishlist refresh on tab change
   *
   * @returns {Object} Methods for adding and removing tab change listener
   */
  const refreshWishlistOnTabChange = () => {
    const _refresh = async () => {
      if (document.hidden) return
      await initWishlist()
    }

    return {
      addWishlistTabChangeListener: () => {
        document.addEventListener('visibilitychange', _refresh)
      },
      removeWishlistTabChangeListener: () => {
        document.removeEventListener('visibilitychange', _refresh)
      },
    }
  }

  /**
   * Watches wishlist items by brand and updates GA wishlist entity
   */
  watch(wishlistItemsByBrand, () => {
    wishlistEntity.value = {
      armani_exchange_wishlist:
        wishlistItemsByBrand.value.get('armani-exchange')?.length ?? 0,
      ea7_wishlist: wishlistItemsByBrand.value.get('ea7')?.length ?? 0,
      emporio_armani_wishlist:
        wishlistItemsByBrand.value.get('emporio-armani')?.length ?? 0,
      giorgio_armani_wishlist:
        wishlistItemsByBrand.value.get('giorgio-armani')?.length ?? 0,
      wishlist_products: wishlistItems.value.length,
      wishlistTotalValue: wishlistItems.value.reduce(
        (acc, { price }) => (acc += price),
        0
      ),
    }
  })

  return {
    initWishlist,
    wishlist: rawWishlist,
    wishlistItems,
    wishlistItemsByBrand,
    wishlistProductCodes,
    nProducts,
    isWishlistEmpty,
    isWishlistLoading,
    isDeletingAll,
    mergeWishlists,
    updateNotifyMe,
    addItem,
    restoreItem,
    removeItem,
    deferredRemoveItem,
    addOrRemoveItem,
    restoreWishlist,
    deleteWishlist,
    refreshWishlistOnTabChange,
  }
}
